/**
 * The permission hook (D14): groups grant `PermissionIdWire[]` under a scope, and a
 * request needs one permission on one resource.
 *
 * Pure — it takes what the caller already resolved (the caller's grants from
 * the token and the group store, or the node's offline cache; D20) and answers
 * one question. The same function runs in the cloud and on the node, so an
 * offline check cannot drift from an online one.
 *
 * The permission ids themselves are the contract's
 * (`@acp/api-contract/permissions.js`); this module is generic over the string
 * so the domain does not depend on the contract. The ids the domain itself
 * requires are named where they are required (`ROW_EDIT_PERMISSION` in
 * `validation.ts`), and a contract test holds them to the contract's list.
 */

/** Where a group's grants apply: the whole tenant, or named sites and studios. */
export type GrantScope =
  | { readonly kind: 'tenant' }
  | { readonly kind: 'resources'; readonly siteIds: readonly string[]; readonly studioIds: readonly string[] };

/** One group's contribution to a caller: its permissions and its scope. */
export type PermissionGrant = {
  readonly permissions: readonly string[];
  readonly scope: GrantScope;
};

/**
 * The resource a request acts on. `null` members mean "not tied to one"
 * (a tenant-wide resource) — only a tenant-scoped grant covers those.
 */
export type ResourceScope = {
  readonly siteId: string | null;
  readonly studioId: string | null;
};

function covers(scope: GrantScope, resource: ResourceScope): boolean {
  if (scope.kind === 'tenant') return true;
  if (resource.siteId !== null && scope.siteIds.includes(resource.siteId)) return true;
  return resource.studioId !== null && scope.studioIds.includes(resource.studioId);
}

/** Whether any grant gives `permission` over `resource`. */
export function isPermitted(grants: readonly PermissionGrant[], permission: string, resource: ResourceScope): boolean {
  return grants.some((grant) => grant.permissions.includes(permission) && covers(grant.scope, resource));
}

/**
 * Whether any grant carries `permission`, in whatever scope: the gate for a listing (then filtered item by item
 * with `isPermitted`), the router's pre-check for a resource route, the SPA's "offer a create" and offline
 * sign-in's `local.login`. One definition, so the hosts and the SPA cannot drift (review 2026-09-27 §2.2).
 */
export function isPermittedAnywhere(grants: readonly Pick<PermissionGrant, 'permissions'>[], permission: string): boolean {
  return grants.some((grant) => grant.permissions.includes(permission));
}
